/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from 'react';
export default function FocusManager({className, children}) {
  let node = null;
  return (
    <div
      className={className}
      onKeyDown={e => trapFocus(node, e)}
      ref={ref => node = ref}
      role="presentation">
      {children}
    </div>
  );
}

/**
 * Querying for tababble elements within the node.
 * @param {Object} node Element where will be searched for tabbables
 * @returns {Array} Array of tabbable elements.
 */
export const getTabbablesDefault = node => {
  const focusableElements = [
    'input:not([disabled]):not([type=hidden])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    'area[href]',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]'
  ];
  focusableElements.push('[tabindex]:not([tabindex="-1"])');
  const TABBABLE_ELEMENT_SELECTOR = focusableElements.join(':not([tabindex="-1"]),');
  const panel = node.querySelector('.help-panel.is-active');
  const header = node.querySelector('.shell-help-center-header');
  if (panel) {
    return [
      ...Array.from(header.querySelectorAll(TABBABLE_ELEMENT_SELECTOR)).filter(el => el !== node),
      ...(Array.from(panel.querySelectorAll(TABBABLE_ELEMENT_SELECTOR)).filter(el => el !== node))
    ];
  }
  return [];
};

// Added in order to handle focus on tabbable elements with custom getTabbables function.
// Reason for getTabbable function is that we needed to filter specific tababble elements
// e.g the only ones that are in active (top) panel.
// Future improvment - utilize FocusManager from react-spectrum.
export const trapFocus = (node, event, getTabbables = getTabbablesDefault) => {
  const {
    type,
    key,
    shiftKey
  } = event;

  let tabbables;
  let tabbable;
  let first;
  let last;
  const targetElement = event.target;
  if (type === 'keydown' || type === 'focus') {
    if (node) {
      // find tabbable elements within container element
      tabbables = getTabbables(node);
      first = tabbables[0] || node;
      last = tabbables[tabbables.length - 1] || node;
    }
  } // If navigating using the tab key,

  if (type === 'keydown' && key === 'Tab') {
    if (node) {
      if (shiftKey) {
        // with focus on first tabbable element, navigating backwards,
        if (targetElement === first || targetElement === node) {
          // focus the last tabbable element
          tabbable = last;
        } // otherwise, with focus on last tabbable element, navigating forwards,
      } else if (targetElement === last || targetElement === node) {
        // focus the first tabbable element.
        tabbable = first;
      }
    }
  } else if (type === 'focus' && targetElement === node) {
    tabbable = first;
  }

  if (!tabbable && !(tabbables || []).includes(document.activeElement)) {
    tabbable = first;
  }

  if (tabbable) {
    event.preventDefault();
    event.stopPropagation();

    if (tabbable !== document.activeElement) {
      tabbable.focus();
    }
  }
};
